import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

const FeatureConfigContext = createContext();

export const useFeatureConfig = () => {
  const context = useContext(FeatureConfigContext);
  if (!context) {
    throw new Error('useFeatureConfig must be used within FeatureConfigProvider');
  }
  return context;
};

// Default feature configuration
const getDefaultFeatures = () => ({
  shiftManagement: {
    enabled: true,
    enabledAt: new Date(),
    enabledBy: 'SYSTEM',
    reason: 'Core feature - Phase 1'
  },
  equipmentManagement: {
    enabled: true,
    enabledAt: new Date(),
    enabledBy: 'SYSTEM',
    reason: 'Core feature - Phase 1'
  },
  teamManagement: {
    enabled: true,
    enabledAt: new Date(),
    enabledBy: 'SYSTEM',
    reason: 'Core feature - Phase 1'
  },
  gamification: {
    enabled: false,
    reason: 'Demo Phase - ยังไม่เปิดให้บริการ (รอพัฒนาให้เสร็จก่อน)'
  },
  marketplace: {
    enabled: false,
    reason: 'Demo Phase - ยังไม่เปิดให้บริการ (รอพัฒนาให้เสร็จก่อน)'
  },
  giftSystem: {
    enabled: false,
    reason: 'Phase 2 - ต้องเปิด Gamification ก่อน'
  },
  mascotRanking: {
    enabled: false,
    reason: 'Phase 2 - ต้องเปิด Gamification ก่อน'
  }
});

export const FeatureConfigProvider = ({ children }) => {
  const [features, setFeatures] = useState(getDefaultFeatures());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const configRef = doc(db, 'featureConfig', 'SYSTEM_FEATURES');
    
    // Set timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('FeatureConfig loading timeout, using defaults');
        setFeatures(getDefaultFeatures());
        setLoading(false);
      }
    }, 5000); // 5 seconds timeout
    
    // Try to get initial config
    getDoc(configRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setFeatures(data.features || getDefaultFeatures());
        } else {
          // Create default config if doesn't exist
          setFeatures(getDefaultFeatures());
        }
        clearTimeout(timeoutId);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching feature config:', err);
        setError(err);
        setFeatures(getDefaultFeatures());
        clearTimeout(timeoutId);
        setLoading(false);
      });

    // Listen for real-time updates
    const unsubscribe = onSnapshot(
      configRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setFeatures(data.features || getDefaultFeatures());
        }
      },
      (err) => {
        console.error('Error listening to feature config:', err);
        setError(err);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const isFeatureEnabled = (featureName) => {
    return features[featureName]?.enabled || false;
  };

  const getFeatureStatus = (featureName) => {
    return features[featureName] || { enabled: false };
  };

  const value = {
    features,
    loading,
    error,
    isFeatureEnabled,
    getFeatureStatus
  };

  return (
    <FeatureConfigContext.Provider value={value}>
      {children}
    </FeatureConfigContext.Provider>
  );
};


